import { Button } from "@caspeco/casper-ui-library";
import { useWebBooking } from "api/api-hooks/use-web-booking";
import { usePaymentRule } from "hooks/use-payment-rule";
import { useTranslation } from "react-i18next";
import { Routes } from "routes";
import { PaymentRule } from "types/payment";

interface ISubmitButtonProps {
    isDisabled: boolean;
    isLoading: boolean;
    trackingId?: string;
}

const SubmitButton = ({ isDisabled, isLoading, trackingId }: ISubmitButtonProps) => {
    const { t } = useTranslation();
    const { data: reservation } = useWebBooking();
    const paymentRule = usePaymentRule();

    const isWaitList = reservation?.isWaitList() ?? false;
    const isRequestFlow = location.pathname.includes(Routes.BOOKING_REQUEST);
    const getButtonText = () => {
        if (isRequestFlow) return t("bookingRequest.send");
        if (isWaitList) return t("waitList.signUpWaitList");
        switch (paymentRule) {
            case PaymentRule.NoShowFee:
                return t("registerPaymentCard");
            case PaymentRule.Prepayment: {
                return t("payAmount", {
                    amount: t("currency.onlyAmount", {
                        value: reservation?.chargeShouldPayAmount,
                    }),
                });
            }
            default:
                return t("book");
        }
    };

    // The reservation is what this button submits, and the label above is read off it too, so
    // a valid contact form alone must not make it pressable. Callers gate on form validity
    // only; before BOOK-3942 that left a window — the whole of the webBooking GET — where a
    // click was swallowed by useFinalizeBooking's guard: no request, no error, no spinner.
    const isReservationLoaded = Boolean(reservation);

    return (
        <Button
            size="xl"
            variant="primary"
            type="submit"
            isLoading={isLoading}
            isDisabled={isDisabled || !isReservationLoaded}
            w="100%"
            data-track-click={trackingId}
        >
            {/* Browser translation swaps bare text nodes for <font>, and the loading spinner then
                makes React remove a node that is no longer there (removeChild crash). */}
            <span>{getButtonText()}</span>
        </Button>
    );
};

export default SubmitButton;
